import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Zap, Trophy, Wrench, Timer, Filter, Users, LayoutDashboard } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../mock';
import { SalesJourney } from '../components/SalesJourney';
import { MySoldCars } from '../components/MySoldCars';
import { useAuth } from '../context/AuthContext';
import { KpiRibbon, DeptCards, AlertCenter, SellerRanking, CountList, HotLeads, MyDeals, Appointments, Panel, RevenueTrendLink } from '../components/DashboardWidgets';

const statusColor = {
  'Concluída': 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20',
  'Em análise': 'text-amber-400 bg-amber-400/10 border border-amber-400/20',
  'Cancelada': 'text-red-400 bg-red-400/10 border border-red-400/20',
};

const tempColor = { 'Quente': 'bg-orange-500 text-orange-500', 'Morno': 'bg-amber-400 text-amber-400', 'Frio': 'bg-sky-400 text-sky-400' };

const fmtResp = (s) => {
  const m = Math.floor((s || 0) / 60);
  return m < 1 ? '<1min' : m < 60 ? `${m}min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
};
const elapsedH = (start) => {
  const ms = Date.now() - new Date(start).getTime();
  if (isNaN(ms) || ms < 0) return '0h 0m';
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
};
const MEDALS = ['text-amber-400 bg-amber-400/15 border-amber-400/40', 'text-slate-300 bg-slate-300/10 border-slate-300/30', 'text-orange-400 bg-orange-400/10 border-orange-400/30'];

const PostSalePanel = ({ cases }) => {
  const novos = cases.filter((c) => c.status === 'New').length;
  const oficina = cases.filter((c) => c.in_workshop);
  const resolvidos = cases.filter((c) => ['Resolved', 'Closed'].includes(c.status)).length;
  return (
    <div className="ent-panel" data-testid="post-sale-panel">
      <div className="ent-colhead" style={{ borderTop: '2px solid #f97316' }}>
        <Wrench className="w-3.5 h-3.5 text-orange-400" />
        <span className="ent-coltitle uppercase tracking-wide">Pós-Venda</span>
        <span className="ent-pill">{cases.length}</span>
        <span className="text-[11px] t-label ml-auto hidden sm:block">Casos e carros na oficina</span>
      </div>
      <div className="ent-ribbon border-0 rounded-none">
        <div><p className="ent-label">Novos</p><p className={`ent-value mt-1 ${novos ? 't-bad' : ''}`} data-testid="ps-kpi-novos">{novos}</p></div>
        <div><p className="ent-label">Na oficina</p><p className={`ent-value mt-1 ${oficina.length ? 't-warn' : ''}`} data-testid="ps-kpi-oficina">{oficina.length}</p></div>
        <div><p className="ent-label">Resolvidos</p><p className="ent-value mt-1 t-ok">{resolvidos}</p></div>
      </div>
      <div className="border-t border-border">
        {oficina.slice(0, 4).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border/60 last:border-0">
            <div className="min-w-0"><p className="text-[12.5px] font-medium truncate">{c.cliente}</p><p className="text-[11px] t-label truncate">{c.veiculo || c.tipo}{c.eta_date ? ` · ETA ${c.eta_date.split('-').reverse().join('/')}` : ''}</p></div>
            <span className="ent-status t-warn num shrink-0"><Timer className="w-3 h-3" /> {elapsedH(c.workshop_started_at)}</span>
          </div>
        ))}
        {oficina.length === 0 && <p className="ent-empty">Nenhum carro na oficina agora</p>}
      </div>
    </div>
  );
};

const ResponseRanking = ({ ranking }) => (
  <div className="rounded-xl border border-border bg-card p-6 animate-fade-up" style={{ animationDelay: '0.12s' }} data-testid="response-ranking">
    <div className="flex items-center gap-2 mb-5">
      <span className="w-9 h-9 rounded-lg bg-primary/12 border border-primary/20 flex items-center justify-center"><Zap className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} /></span>
      <div>
        <h3 className="font-display font-bold text-lg tracking-tight text-foreground">Ranking de Resposta</h3>
        <p className="text-[11px] text-muted-foreground">Vendedores mais rápidos com leads no CRM</p>
      </div>
    </div>
    <div className="space-y-3">
      {ranking.map((r, i) => (
        <div key={r.vendedor} data-testid={`ranking-row-${i}`} className={`flex items-center gap-3 rounded-lg border p-3 ${i === 0 ? 'border-amber-400/40 bg-amber-400/[0.05]' : 'border-border/70'}`}>
          <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-black shrink-0 ${MEDALS[i] || 'text-muted-foreground bg-muted/40 border-border'}`}>
            {i === 0 ? <Trophy className="w-3.5 h-3.5" /> : `${i + 1}º`}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate">{r.vendedor}</p>
            <p className="text-[10px] text-muted-foreground">{r.moves} movimentação(ões) · {r.aplicacoes} aplicação(ões)</p>
          </div>
          <span className={`text-sm font-black tabular-nums ${r.avg_idle_seconds <= 900 ? 'text-emerald-400' : r.avg_idle_seconds <= 86400 ? 'text-amber-400' : 'text-red-400'}`}>{fmtResp(r.avg_idle_seconds)}</span>
        </div>
      ))}
      {ranking.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-6">Sem movimentações de leads ainda.<br />O ranking aparece quando os vendedores começarem a mover leads no CRM.</p>}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-strong rounded-lg px-3 py-2 shadow-glow">
      <p className="text-[10px] tracking-wider text-muted-foreground uppercase">{label}</p>
      <p className="text-sm font-semibold text-foreground">{formatCurrency(payload[0].value)}</p>
    </div>
  );
};

const PERSONA_LABEL = {
  salesperson: 'Meu painel de vendas', bdc: 'Meu painel BDC', sales_manager: 'Painel de Vendas',
  bdc_manager: 'Painel BDC', service_manager: 'Painel de Serviço', postsale_manager: 'Painel de Pós-Venda',
  finance_manager: 'Painel Financeiro', executive: 'Painel gerencial', detail: 'Painel Detail',
};

const RevenueChart = ({ chart = [], delta }) => (
  <div className="xl:col-span-2 rounded-xl border border-border bg-card p-5 relative overflow-hidden">
    <div className="absolute inset-0 tech-grid-fine opacity-30 pointer-events-none" />
    <div className="relative flex items-center justify-between mb-6">
      <div>
        <h3 className="font-display font-bold text-lg tracking-tight text-foreground">Faturamento</h3>
        <p className="text-sm text-muted-foreground">Últimos 7 meses</p>
      </div>
      <RevenueTrendLink delta={delta} />
    </div>
    <div className="relative h-52 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chart} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="barBlue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(190 95% 55%)" stopOpacity={0.95} />
              <stop offset="100%" stopColor="hsl(217 91% 55%)" stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: 'hsl(216 14% 55%)', fontSize: 12 }} dy={8} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(217 91% 60% / 0.06)' }} />
          <Bar dataKey="faturamento" radius={[6, 6, 0, 0]} maxBarSize={46} animationDuration={1000}>
            {chart.map((e, i) => <Cell key={i} fill="url(#barBlue)" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Overview = () => {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [psCases, setPsCases] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/dashboard/me');
        setDash(data);
        if (data.persona === 'executive') {
          const [rk, ps] = await Promise.all([api.get('/crm/response-ranking'), api.get('/post_sale_cases')]);
          setRanking(rk.data); setPsCases(ps.data);
        }
      } catch (e) { /* protected route */ }
    })();
  }, []);

  if (!dash) return <p className="ent-empty" data-testid="dash-loading">Carregando seu painel...</p>;
  const b = dash.blocks || {};
  const persona = dash.persona;

  return (
    <div className="space-y-4" data-testid={`dashboard-${persona}`}>
      <div className="flex items-center gap-2.5 flex-wrap">
        <LayoutDashboard className="w-4 h-4 text-primary" />
        <h2 className="font-display font-black text-base uppercase tracking-tight">{PERSONA_LABEL[persona] || 'Painel'}</h2>
        <span className="ent-tag" data-testid="dash-persona-tag">{dash.role}</span>
        <span className="text-[11px] t-label ml-auto">{dash.user} · referência {dash.month}</span>
      </div>

      <KpiRibbon kpis={dash.kpis} />

      {persona === 'salesperson' && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <CountList title="Meu funil por etapa" icon={Filter} data={b.funnel} empty="Nenhum lead ativo" />
            <HotLeads leads={b.hot_leads} />
            <Appointments items={b.appointments} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <MyDeals deals={b.my_deals} />
            <SellerRanking rows={b.ranking} me={dash.user} position={b.my_position} total={b.ranking_total} />
          </div>
          <MySoldCars />
        </>
      )}

      {persona === 'bdc' && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <CountList title="Meus leads por fonte" icon={Filter} data={b.by_source} empty="Nenhum lead atribuído" />
            <Panel title="Sem primeiro contato" icon={Timer} testid="dash-no-contact">
              {(b.no_contact || []).length ? (
                <div className="space-y-1.5">
                  {b.no_contact.map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      <span className="text-[12.5px] font-medium flex-1 truncate">{l.nome || l.cliente}</span>
                      <span className="ent-tag t-warn">{l.lead_source || l.origem || '—'}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[12px] t-ok">Todos os leads já tiveram o primeiro contato.</p>}
            </Panel>
            <Appointments items={b.appointments} />
          </div>
          <ResponseRanking ranking={(b.response_ranking || []).slice(0, 5)} />
        </>
      )}

      {persona === 'sales_manager' && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <CountList title="Deals por etapa" icon={Filter} data={b.deals_by_stage} empty="Nenhum deal ativo" />
            <Panel title="Atenção do time" icon={Zap} testid="dash-sales-alerts">
              <div className="space-y-1.5 text-[12.5px]">
                <div className="flex justify-between"><span className="t-label">Leads sem atendimento</span><span className={b.unattended ? 't-bad font-semibold' : 't-ok'}>{b.unattended || 0}</span></div>
                <div className="flex justify-between"><span className="t-label">Estoque 60+ dias</span><span className={b.aging_60 ? 't-warn font-semibold' : 't-ok'}>{b.aging_60 || 0}</span></div>
                <div className="flex justify-between"><span className="t-label">Veículos sem custo</span><span className={b.no_cost ? 't-warn font-semibold' : 't-ok'}>{b.no_cost || 0}</span></div>
                <div className="flex justify-between"><span className="t-label">Estoque disponível</span><span>{b.store?.estoque || 0}</span></div>
              </div>
            </Panel>
            <SalesJourney />
          </div>
          <SellerRanking rows={b.ranking} me={dash.user} />
        </>
      )}

      {persona === 'bdc_manager' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <CountList title="Leads do mês por fonte" icon={Filter} data={b.bdc?.by_source} />
          <Panel title="Agentes" icon={Users} testid="dash-agents">
            <table className="ent-table">
              <thead><tr><th>Agente</th><th className="text-right">Leads</th><th className="text-right">Sem contato</th></tr></thead>
              <tbody>
                {(b.bdc?.per_agent || []).map((a) => (
                  <tr key={a.agente}><td className="font-medium">{a.agente}</td><td className="num text-right">{a.leads}</td>
                    <td className={`num text-right ${a.sem_contato ? 't-bad' : 't-ok'}`}>{a.sem_contato}</td></tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <ResponseRanking ranking={(b.bdc?.response_ranking || []).slice(0, 5)} />
        </div>
      )}

      {persona === 'service_manager' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <CountList title="Inspeções por status" icon={Wrench} data={b.service?.inspections} />
          <Panel title="Resumo da loja" icon={Trophy} testid="dash-store">
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex justify-between"><span className="t-label">Vendas no mês</span><span className="font-semibold">{b.store?.vendas_mes || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Faturamento</span><span className="num">{formatCurrency(b.store?.faturamento_mes || 0)}</span></div>
              <div className="flex justify-between"><span className="t-label">Estoque disponível</span><span>{b.store?.estoque || 0}</span></div>
            </div>
          </Panel>
          <SalesJourney />
        </div>
      )}

      {persona === 'postsale_manager' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <CountList title="Casos abertos por tipo" icon={Filter} data={b.post_sale?.by_type} empty="Nenhum caso aberto" />
          <Panel title="Resumo da loja" icon={Trophy} testid="dash-store">
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex justify-between"><span className="t-label">Vendas no mês</span><span className="font-semibold">{b.store?.vendas_mes || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Faturamento</span><span className="num">{formatCurrency(b.store?.faturamento_mes || 0)}</span></div>
              <div className="flex justify-between"><span className="t-label">Estoque disponível</span><span>{b.store?.estoque || 0}</span></div>
            </div>
          </Panel>
          <SalesJourney />
        </div>
      )}

      {persona === 'finance_manager' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <Panel title="Fundings" icon={Zap} testid="dash-fin-funding">
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex justify-between"><span className="t-label">Pendentes</span><span className="font-semibold">{b.finance?.funding_pending || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Pendentes +7 dias</span><span className={b.finance?.funding_late ? 't-bad font-semibold' : 't-ok'}>{b.finance?.funding_late || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Recebido no mês</span><span className="num t-ok">{formatCurrency(b.finance?.funded_amount_month || 0)}</span></div>
            </div>
          </Panel>
          <Panel title="Payoffs & Garantias" icon={Trophy} testid="dash-fin-payoff">
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex justify-between"><span className="t-label">Payoffs a pagar</span><span className="font-semibold">{b.finance?.payoff_pending || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Valor dos payoffs</span><span className="num t-warn">{formatCurrency(b.finance?.payoff_pending_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="t-label">Garantias vendidas</span><span>{b.finance?.warranties_sold || 0}</span></div>
            </div>
          </Panel>
          <Panel title="Resumo da loja" icon={Trophy} testid="dash-store">
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex justify-between"><span className="t-label">Vendas no mês</span><span className="font-semibold">{b.store?.vendas_mes || 0}</span></div>
              <div className="flex justify-between"><span className="t-label">Faturamento</span><span className="num">{formatCurrency(b.store?.faturamento_mes || 0)}</span></div>
              <div className="flex justify-between"><span className="t-label">Gross do mês</span><span className="num t-ok">{formatCurrency(b.store?.gross_mes || 0)}</span></div>
            </div>
          </Panel>
        </div>
      )}

      {persona === 'executive' && (
        <>
          <DeptCards departments={b.departments} />
          <SalesJourney />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <RevenueChart chart={b.sales_chart} delta={b.store?.faturamento_delta} />
            <AlertCenter alerts={b.alerts} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <div className="space-y-3">
              <ResponseRanking ranking={ranking.slice(0, 5)} />
              <PostSalePanel cases={psCases} />
            </div>
            <div className="xl:col-span-2 space-y-3">
              <SellerRanking rows={b.ranking} me={dash.user} />
              <CountList title="Deals por etapa" icon={Filter} data={b.deals_by_stage} empty="Nenhum deal ativo" />
            </div>
          </div>
        </>
      )}

      {persona === 'detail' && <SalesJourney />}
    </div>
  );
};

export default Overview;
